import { Box } from '@mui/material';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout, Header } from '@/components/layout';
import {
  SiteInfoForm,
  MapSection,
  MaterialsSection,
  PhotosSection,
} from '../components';
import { useSiteData, useSiteForm, useSiteMaterials } from '../hooks';
import { createPlatformAwareMapUrl } from '@/utils/platformUtils';
import { useToast } from '@/components/feedback';
import { ConfirmModal } from '@/components';
import { useSitesStore } from '../store';
import { useNavigate } from '@tanstack/react-router';

interface SiteManagementPageProps {
  searchParams: {
    projectId: number;
    siteId: number;
  };
}

export const SiteManagementPage: FC<SiteManagementPageProps> = ({
  searchParams,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const { siteId, projectId } = searchParams;

  // Custom hooks for data management
  const {
    project,
    currentSite,
    materials,
    siteMaterials,
    photos,
    loading,
    actions: {
      updateSite: onUpdateSite,
      updateSiteMaterials,
      deleteSiteMaterials,
      deletePhoto,
    },
  } = useSiteData({ siteId, projectId });

  // Form management hook
  const {
    siteName,
    setSiteName,
    currentPriority,
    setCurrentPriority,
    currentState,
    setCurrentState,
    coordinates,
    setCoordinates,
    latitudeInput,
    setLatitudeInput,
    longitudeInput,
    setLongitudeInput,
    currentDescription,
    setCurrentDescription,
    currentRequiredChanges,
    setCurrentRequiredChanges,
    siteOldName,
    updateStatus,
    updateFieldStatus,
    updateSiteField,
  } = useSiteForm({ currentSite, onUpdateSite });

  // Materials management hook
  const {
    localSiteMaterials,
    addMaterialsOpenModal,
    onUpdateMaterial,
    onDeleteMaterial,
    onDeleteMaterials,
    onChangeSiteMaterials,
    addAllMaterials,
    openAddMaterialsModal,
    handleCloseMaterialsModal,
    handleSaveMaterials,
  } = useSiteMaterials({
    siteId,
    siteMaterials,
    materials,
    onUpdateSiteMaterials: updateSiteMaterials,
    onDeleteSiteMaterials: deleteSiteMaterials,
  });
  const navigate = useNavigate();
  const { deleteSite } = useSitesStore(state => state.actions);
  // Local state for map functionality
  const [isActiveLock, setIsActiveLock] = useState<boolean>(true);
  const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);
  const [mapKey] = useState<string>(
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
      process.env.VITE_GOOGLE_MAPS_API_KEY ||
      ''
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false);
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        setIsFullscreen(true);
      } else {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Generic save handler to reduce code duplication
  const handleSaveField = async (
    fieldName:
      | 'siteName'
      | 'priority'
      | 'currentState'
      | 'description'
      | 'requiredChanges'
      | 'coordinates',
    updateData: any,
    successMessage?: string
  ) => {
    updateFieldStatus(fieldName, {
      updating: true,
      error: false,
      updated: false,
    });

    try {
      const success = await updateSiteField(updateData);
      if (success) {
        updateFieldStatus(fieldName, {
          updating: false,
          error: false,
          updated: true,
        });
        if (successMessage) {
          showSuccess(successMessage);
        }
        setTimeout(
          () => updateFieldStatus(fieldName, { updated: false }),
          2000
        );
        return true;
      } else {
        updateFieldStatus(fieldName, {
          updating: false,
          error: true,
          updated: false,
        });
        showError(t('update_failed') || 'Update failed');
        return false;
      }
    } catch (error) {
      updateFieldStatus(fieldName, {
        updating: false,
        error: true,
        updated: false,
      });
      console.error(`Error updating ${fieldName}:`, error);
      showError(t('update_failed') || 'Update failed');
      return false;
    }
  };

  // Map handlers
  const onChangeCoordinates = async (coords: { lat: number; lng: number }) => {
    setCoordinates(coords);
    setLatitudeInput(coords.lat.toString());
    setLongitudeInput(coords.lng.toString());

    await handleSaveField(
      'coordinates',
      { coordinates: coords },
      t('coordinates_updated') || 'Coordinates updated successfully'
    );
  };

  const setValidLatitude = () => {
    if (!latitudeInput) return;
    const filteredValue = latitudeInput.replace(/[^0-9.,-]/g, '');
    const formattedValue = filteredValue.replace(',', '.');
    const lat = parseFloat(formattedValue);
    if (!isNaN(lat)) {
      const newCoords = { lat, lng: coordinates.lng };
      onChangeCoordinates(newCoords);
    }
  };

  const setValidLongitude = () => {
    if (!longitudeInput) return;
    const filteredValue = longitudeInput.replace(/[^0-9.,-]/g, '');
    const formattedValue = filteredValue.replace(',', '.');
    const lng = parseFloat(formattedValue);
    if (!isNaN(lng)) {
      const newCoords = { lat: coordinates.lat, lng };
      onChangeCoordinates(newCoords);
    }
  };

  const onChangeLatitude = (value: string) => {
    setLatitudeInput(value);
  };

  const onChangeLongitude = (value: string) => {
    setLongitudeInput(value);
  };

  const toggleLock = () => {
    setIsActiveLock(!isActiveLock);
  };

  const getCoordinates = async () => {
    if (!navigator.geolocation) {
      alert(
        t('geolocation_not_supported') ||
          'Geolocation is not supported by this browser.'
      );
      return;
    }

    if ('permissions' in navigator) {
      try {
        const permission = await (navigator as any).permissions.query({
          name: 'geolocation',
        });
        if (permission.state === 'denied') {
          alert(
            t('geolocation_permission_denied') ||
              'Geolocation permission denied. Please allow location access in your browser settings.'
          );
          return;
        }
      } catch (error) {
        console.warn('Could not query geolocation permission:', error);
      }
    }

    if (navigator.geolocation) {
      setIsGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        position => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;

          if (!isNaN(latitude) && !isNaN(longitude)) {
            onChangeCoordinates({ lat: latitude, lng: longitude });
          }
          setIsGettingLocation(false);
        },
        error => {
          console.error('Error getting location:', error.message);
          setIsGettingLocation(false);
          alert(
            t('geolocation_permission_denied') ||
              'Please allow location access and try again.'
          );
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  };

  const createMapLink = (lat: string, lng: string) => {
    // Use platform-aware map URL generation with native app preference
    return createPlatformAwareMapUrl(lat, lng, {
      preferNative: true,
      preferredApp: 'native',
    });
  };

  // Site info form handlers
  const onSaveName = async () => {
    if (siteName !== currentSite?.siteName) {
      await handleSaveField(
        'siteName',
        { siteName },
        t('site_name_updated') || 'Site name updated successfully'
      );
    }
  };

  const onSavePriority = async (priority: string) => {
    const priorityNumber = parseInt(priority);
    if (priorityNumber !== currentSite?.priority) {
      await handleSaveField(
        'priority',
        { priority: priorityNumber },
        t('priority_updated') || 'Priority updated successfully'
      );
    }
  };

  const onSaveCurrentState = async (state: string) => {
    if (state !== currentSite?.currentState) {
      await handleSaveField(
        'currentState',
        { currentState: state },
        t('state_updated') || 'State updated successfully'
      );
    }
  };

  const onSaveDescription = async () => {
    if (currentDescription !== currentSite?.description) {
      await handleSaveField(
        'description',
        { description: currentDescription },
        t('description_updated') || 'Description updated successfully'
      );
    }
  };

  const onSaveRequiredChanges = async () => {
    if (currentRequiredChanges !== currentSite?.requiredChanges) {
      await handleSaveField(
        'requiredChanges',
        { requiredChanges: currentRequiredChanges },
        t('required_changes_updated') || 'Required changes updated successfully'
      );
    }
  };

  // Photo handlers
  const deleteImage = async (photoId: number) => {
    try {
      await deletePhoto(photoId, siteId);
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const onPhotosChanged = () => {
    // This function is called when photos are added/changed
    // Currently the PhotoEdit component manages its own state, but we could
    // sync with the global state here if needed for consistency
    // console.log('Photos updated:', updatedPhotoList.length, 'photos');
  };

  // Transform photos for PhotoEdit component (already normalized by useSiteData hook)
  const photoList = useMemo(() => {
    return photos; // Photos are already normalized to PhotoData format by useSiteData
  }, [photos]);

  const handleDeleteSite = () => {
    setShowModalConfirm(true);
  };
  const tryDeleteSite = async () => {
    const success = await deleteSite(siteId);
    if (success) {
      showSuccess(t('site_deleted_success'));
      navigate({
        to: `/projects/${projectId}/sites/`,
        search: {},
      });
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          minHeight: ['100vh', '100dvh', '100svh'],
          pb: 8,
          width: '100%',
        }}
      >
        <Header
          title={siteOldName ? siteOldName : t('new_site_edit_site')}
          hideBackButton={false}
          linkBack={`/projects/${projectId}/sites`}
          handleDelete={handleDeleteSite}
        />

        <Box>
          {/* Site Information Form */}
          <SiteInfoForm
            siteName={siteName}
            setSiteName={setSiteName}
            currentPriority={currentPriority}
            setCurrentPriority={setCurrentPriority}
            currentState={currentState}
            setCurrentState={setCurrentState}
            currentDescription={currentDescription}
            setCurrentDescription={setCurrentDescription}
            currentRequiredChanges={currentRequiredChanges}
            setCurrentRequiredChanges={setCurrentRequiredChanges}
            updateStatus={updateStatus}
            onSaveName={onSaveName}
            onSavePriority={onSavePriority}
            onSaveCurrentState={onSaveCurrentState}
            onSaveDescription={onSaveDescription}
            onSaveRequiredChanges={onSaveRequiredChanges}
          />

          {/* Map Section */}
          <MapSection
            mapKey={mapKey}
            coordinates={coordinates}
            latitudeInput={latitudeInput}
            longitudeInput={longitudeInput}
            isActiveLock={isActiveLock}
            isFullscreen={isFullscreen}
            isGettingLocation={isGettingLocation}
            updateStatus={updateStatus}
            onChangeCoordinates={onChangeCoordinates}
            onChangeLatitude={onChangeLatitude}
            onChangeLongitude={onChangeLongitude}
            setValidLatitude={setValidLatitude}
            setValidLongitude={setValidLongitude}
            toggleLock={toggleLock}
            getCoordinates={getCoordinates}
            createMapLink={createMapLink}
          />

          {/* Materials Section */}
          <MaterialsSection
            localSiteMaterials={localSiteMaterials}
            loading={loading.materials !== 'idle'}
            addMaterialsOpenModal={addMaterialsOpenModal}
            project={project}
            materials={materials}
            onChangeSiteMaterials={onChangeSiteMaterials}
            onUpdateMaterial={onUpdateMaterial}
            onDeleteMaterial={onDeleteMaterial}
            onDeleteMaterials={onDeleteMaterials}
            openAddMaterialsModal={() =>
              openAddMaterialsModal(project, loading.materials !== 'idle')
            }
            addAllMaterials={() =>
              addAllMaterials(project, loading.materials !== 'idle')
            }
            handleCloseMaterialsModal={handleCloseMaterialsModal}
            handleSaveMaterials={handleSaveMaterials}
          />

          {/* Photos Section */}
          <PhotosSection
            photoList={photoList}
            loading={loading.photos !== 'idle'}
            siteId={siteId}
            deleteImage={deleteImage}
            onPhotosChanged={onPhotosChanged}
          />
        </Box>
      </Box>
      <ConfirmModal
        cancel={() => setShowModalConfirm(false)}
        confirm={() => {
          tryDeleteSite();
        }}
        title={t('delete_site_confirmation_title')}
        show={showModalConfirm}
      />
    </Layout>
  );
};
